/**
 * Pull-to-refresh: the arithmetic, so the gesture can be tested without a touchscreen.
 *
 * Three rules decide whether a downward drag is a refresh or a scroll, and getting any of them wrong
 * makes the page feel broken rather than makes the feature missing:
 *
 * - **Only from the very top.** A pull that starts mid-page is a scroll. Legacy checks `scrollY === 0`
 *   at the moment the finger lands, not continuously — a page that reaches the top mid-drag must not
 *   suddenly turn that drag into a refresh under the customer's thumb.
 * - **Resistance.** The indicator follows the finger at a fraction of the distance, so the gesture has
 *   somewhere to go past the trigger point instead of stopping dead.
 * - **A threshold, and a maximum.** Below the threshold, releasing does nothing and the indicator
 *   springs back; the pull cannot be dragged arbitrarily far.
 */

/** How far the finger must travel before releasing refreshes. */
export const PULL_THRESHOLD = 70
/** How far the indicator may travel, however far the finger goes. */
export const PULL_MAX = 120
/** The indicator moves this fraction of the finger's distance. */
export const PULL_RESISTANCE = 0.5

export interface PullState {
  /** Where the finger landed, or `null` when this gesture is not a candidate. */
  startY: number | null
  distance: number
}

export const NOT_PULLING: PullState = { startY: null, distance: 0 }

/**
 * A finger landing.
 *
 * `scrollY` is read once, here. Re-reading it during the move is what lets a scroll become a refresh
 * halfway through.
 */
export function onTouchStart(clientY: number, scrollY: number): PullState {
  return scrollY <= 0 ? { startY: clientY, distance: 0 } : NOT_PULLING
}

export function onTouchMove(state: PullState, clientY: number): PullState {
  if (state.startY === null) return state
  const travelled = clientY - state.startY
  // Upward movement is a scroll, not a negative pull.
  if (travelled <= 0) return { ...state, distance: 0 }
  return { ...state, distance: Math.min(PULL_MAX, travelled * PULL_RESISTANCE) }
}

/** Whether releasing now should refresh. */
export function shouldRefresh(state: PullState): boolean {
  return state.startY !== null && state.distance >= PULL_THRESHOLD
}

/** How far to draw the indicator, as a fraction of the way to the threshold. */
export function pullProgress(state: PullState): number {
  return Math.min(1, state.distance / PULL_THRESHOLD)
}
